import React, { useEffect, useState, useMemo } from "react";
import "./collectionsHomeScreen.css";
import { logLoadTrace } from "../../../../firebase";
import CustomDataGrid from "../../../../components/dms_common/CustomDataGrid";
import Header from "../../../../components/dms_common/Header";
import {
  fetchAllData,
  fetchPaymentSummary,
} from "../../services/collectionService";
import { Loader } from "../../../../components/loader/Loader";
import { useNavigate } from "react-router-dom";
import { OutletCollectionDetails } from "../../../../types/types";
import {
  collectionsHomeScreenTableMetaData,
  collectionStatusMapping,
  PaymentSummary,
} from "../../collectionUtils";
import CollectionSummary from "../../components/collectionSummary/CollectionSummary";
import GenericFilterSelect from "../../components/collectionHomeScreenFilter/GenericFilterSelect";

// Default payment summary object
const DEFAULT_PAYMENT_SUMMARY: PaymentSummary = {
  paidByCash: 0,
  paidByUpi: 0,
  paidByCheque: 0,
  paidByNeft: 0,
};

const CollectionsHomeScreen = () => {
  const [allData, setAllData] = useState<OutletCollectionDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState<OutletCollectionDetails[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterSearchValue, setFilterSearchValue] = useState("");
  const [activeFilterType, setActiveFilterType] = useState("status");
  // Selected options for each filter type
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string[]>
  >({
    status: [],
    customer: [],
  });
  const [collectionSummary, setCollectionSummary] = useState<PaymentSummary>(DEFAULT_PAYMENT_SUMMARY);

  const navigate = useNavigate();

  // Dynamically generate filterData based on allData
  const filterData = useMemo(() => {
    if (!allData || allData.length === 0) {
      return {
        status: {},
        customer: {},
      };
    }

    // Count occurrences of each status
    const statusCounts: Record<string, number> = {};
    const customerCounts: Record<string, number> = {};

    allData.forEach((item) => {
      // Count status occurrences
      if (item && item.status) {
        const status = item.status.toUpperCase(); // Normalize to uppercase
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      }

      // Count customer/outletType occurrences (only if not empty)
      if (item && item.outletType && item.outletType.trim() !== "") {
        const outletType = item.outletType;
        customerCounts[outletType] = (customerCounts[outletType] || 0) + 1;
      }
    });

    return {
      status: statusCounts,
      customer: customerCounts,
    };
  }, [allData]);

  const fetchData = async () => {
    try {
      const allData = await fetchAllData();
      // Add null check for allData
      setAllData(allData || []);
      setFilteredData(allData || []);

      // Consider adding a check for empty array
      if (Array.isArray(allData) && allData.length === 0) {
        console.log("No data available");
      }
    } catch (e) {
      console.error("Error fetching collection data:", e);
      // Set empty arrays on error
      setAllData([]);
      setFilteredData([]);

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    logLoadTrace(fetchData, "collection_homescreen_page_load_time");
  }, []);

  useEffect(() => {
    const calculateCollectionSummary = async () => {
      try {
        const summary = await fetchPaymentSummary();
        // Add null check for summary
        setCollectionSummary(summary || DEFAULT_PAYMENT_SUMMARY);
      } catch (e) {
        console.error("Error fetching payment summary:", e);
        // Set default values on error
        setCollectionSummary(DEFAULT_PAYMENT_SUMMARY);
      }
    };
    logLoadTrace(calculateCollectionSummary, "collection_payment_summary_load_time");
  }, []);


  // Search and filter logic
  useEffect(() => {
    let result = [...allData];

    // Apply search text filtering
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      result = result.filter((row: OutletCollectionDetails) => {
        return (
          (row.outletCode?.toLowerCase().includes(searchTextLower) || false) ||
          (row.outletName?.toLowerCase().includes(searchTextLower) || false) ||
          (row.outletType?.toLowerCase().includes(searchTextLower) || false) ||
          (row.creditNoteAmount?.toString().toLowerCase().includes(searchTextLower) || false) ||
          (row.status?.toLowerCase().includes(searchTextLower) || false) ||
          (row.balanceAmount?.toString().toLowerCase().includes(searchTextLower) || false)
        );
      });
    }

    // Apply status filter (case-insensitive)
    if (selectedOptions.status.length > 0) {
      result = result.filter((row) =>
        selectedOptions.status.some((status) =>
          status.toLowerCase() === row.status?.toLowerCase()
        )
      );
    }

    // Apply customer filter (skip if outletType is empty or handle differently)
    if (selectedOptions.customer.length > 0) {
      result = result.filter((row) =>
        row.outletType
          ? selectedOptions.customer.includes(row.outletType)
          : false // Skip rows with empty outletType
      );
    }

    setFilteredData(result);
  }, [searchText, allData, selectedOptions]);

  const handleRedirect = async (
    redirectUrl: string,
    rowData: OutletCollectionDetails
  ) => {
    if (!rowData) return;

    navigate(redirectUrl, {
      state: {
        outletCode: rowData?.outletCode,
      },
    });
  };



  // Handler for when an option is selected/deselected
  const handleOptionChange = (filterType: string, option: string) => {
    setSelectedOptions((prev) => {
      const currentOptions = prev[filterType] || [];
      const isSelected = currentOptions.includes(option);

      return {
        ...prev,
        [filterType]: isSelected
          ? currentOptions.filter((item) => item !== option)
          : [...currentOptions, option],
      };
    });
  };

  return (
    <div className="collections-home-screen-container">
      <Header title={"Collection"} searchFunction={setSearchText} />
      <div className="collection-summary-container">
        <CollectionSummary collectionSummary={collectionSummary} />
        <GenericFilterSelect
          isOpen={isFilterOpen}
          setIsOpen={setIsFilterOpen}
          searchValue={filterSearchValue}
          setSearchValue={setFilterSearchValue}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          activeFilterType={activeFilterType}
          setActiveFilterType={setActiveFilterType}
          filterData={filterData}
          onOptionChange={handleOptionChange}
          displayLabel="All Filters"
        />
      </div>
      {loading ? (
        <Loader />
      ) : (
        <CustomDataGrid
          rows={filteredData || []}
          columnsMetaData={collectionsHomeScreenTableMetaData()}
          tableWidth={0.76}
          redirectUrl="outletDetails"
          redirectUrlFunction={handleRedirect}
          orderStatusConfig={collectionStatusMapping()}
        />
      )}
    </div>
  );
};

export default CollectionsHomeScreen;